import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listInboxPdfs, readInboxPdf, moveInboxToDone, getInboxHandle, getInboxAccess, requestInboxAccess,
  type InboxPdf, type AccessState,
} from '@/lib/fileTargets';

/**
 * Welle 12 (pre116): Eingangsordner „zur Unterschrift".
 * Pollt den gemerkten Eingangsordner und meldet neue PDFs. Der Berater kann je
 * Dokument abbrechen, es ansehen oder direkt zur Unterschrift freigeben. Bei
 * Freigabe wird `onSign(file)` aufgerufen (im normalen Client: rtc.submitSignature)
 * und die Quell-PDF nach „erledigt" verschoben.
 *
 * Wiederverwendbar in normalem Client UND Companion – `onSign`/`canSign` kommen vom Aufrufer.
 */
export function SignInbox({ onSign, canSign, onCount }: { onSign: (file: File) => void; canSign: boolean; onCount?: (n: number) => void }) {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [access, setAccess] = useState<AccessState>('none');
  const [pending, setPending] = useState<InboxPdf[]>([]);
  useEffect(() => { onCount?.(pending.length); }, [pending.length, onCount]);
  const [busy, setBusy] = useState<string | null>(null);
  const dismissedRef = useRef<Set<string>>(new Set());
  const handleRef = useRef<any>(null);

  const refresh = useCallback(async () => {
    const h = await getInboxHandle();
    handleRef.current = h;
    if (!h) { setConfigured(false); setPending([]); return; }
    setConfigured(true);
    const acc = await getInboxAccess(h);
    setAccess(acc);
    if (acc !== 'granted') { setPending([]); return; }
    const list = await listInboxPdfs();
    setPending(list.filter((p) => !dismissedRef.current.has(p.key)));
  }, []);

  useEffect(() => {
    void refresh();
    const iv = setInterval(refresh, 5000);
    return () => clearInterval(iv);
  }, [refresh]);

  const confirmAccess = async () => {
    if (!handleRef.current) return;
    await requestInboxAccess(handleRef.current);
    await refresh();
  };

  const view = async (name: string) => {
    const f = await readInboxPdf(name);
    if (!f) return;
    const url = URL.createObjectURL(f);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const release = async (p: InboxPdf) => {
    if (!canSign) return;
    setBusy(p.key);
    try {
      const f = await readInboxPdf(p.name);
      if (f) {
        onSign(f);
        await moveInboxToDone(p.name);
        dismissedRef.current.add(p.key);
        setPending((cur) => cur.filter((x) => x.key !== p.key));
      }
    } finally { setBusy(null); }
  };

  const dismiss = (p: InboxPdf) => {
    dismissedRef.current.add(p.key);
    setPending((cur) => cur.filter((x) => x.key !== p.key));
  };

  // Nichts konfiguriert oder nichts zu melden → unsichtbar.
  if (!configured) return null;
  if (access !== 'granted' && pending.length === 0) {
    // Ordner gewählt, aber Zugriff noch nicht bestätigt: nur Hinweis, wenn relevant — hier still.
    return null;
  }
  if (pending.length === 0) return null;

  return (
    <div style={wrap}>
      <div style={head}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>Zur Unterschrift gefunden</span>
        <span style={countPill}>{pending.length}</span>
      </div>
      {access !== 'granted' && (
        <button onClick={confirmAccess} style={confirmBtn}>Ordnerzugriff bestätigen</button>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
        {pending.slice(0, 5).map((p) => (
          <div key={p.key} style={row}>
            <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.name}>
              {p.name}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button onClick={() => dismiss(p)} style={btnGhost}>Abbrechen</button>
              <button onClick={() => view(p.name)} style={btnGhost}>Ansehen</button>
              <button onClick={() => release(p)} disabled={!canSign || busy === p.key} style={{ ...btnPrimary, opacity: !canSign || busy === p.key ? 0.5 : 1 }}
                title={canSign ? 'Unterschrift mit diesem Dokument starten' : 'Nur während eines aktiven Gesprächs möglich'}>
                {busy === p.key ? '…' : 'Zur Unterschrift freigeben'}
              </button>
            </div>
          </div>
        ))}
        {pending.length > 5 && <div style={{ fontSize: 11, color: 'var(--muted, #8aa)' }}>+{pending.length - 5} weitere</div>}
      </div>
    </div>
  );
}

const wrap: React.CSSProperties = {
  position: 'fixed', left: 16, bottom: 16, zIndex: 1300, width: 320, maxWidth: '90vw',
  background: 'var(--bg-elev-1, #161616)', border: '1px solid var(--tenant-accent, #7c3aed)',
  borderRadius: 12, padding: 12, boxShadow: '0 8px 30px rgba(0,0,0,.4)',
};
const head: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const countPill: React.CSSProperties = { background: 'var(--tenant-accent, #7c3aed)', color: '#fff', borderRadius: 999, fontSize: 11, fontWeight: 700, padding: '1px 7px' };
const row: React.CSSProperties = { borderTop: '1px solid var(--hairline, #232323)', paddingTop: 8 };
const btnGhost: React.CSSProperties = { border: '1px solid var(--hairline, #232323)', background: 'transparent', color: 'var(--fg, #eee)', borderRadius: 7, padding: '5px 10px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
const btnPrimary: React.CSSProperties = { border: 'none', background: 'var(--tenant-accent, #7c3aed)', color: '#fff', borderRadius: 7, padding: '5px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const confirmBtn: React.CSSProperties = { ...btnPrimary, marginTop: 6, width: '100%' };
